import { ColumnMapping, ExcelProcessorConfig, HeaderConfig, ParsedExcelData, ValidationError } from '@/lib/excel';
import { AccountModelInput, AccountRole } from '@/types/schemas';

const HEADERS = {
  CODE: 'Tài khoản',
  NAME: 'Tên tài khoản',
  PARENT_ACCOUNT_CODE: 'Tài khoản mẹ',
  TK_SO_CAI: 'Tk sổ cái',
  TK_CHI_TET: 'Tk chi tiết',
  BAC_TK: 'Bậc tk',
  CURRENCY_CODE: 'Ngoại tệ',
  AR_AP_TRACKING_TYPE: 'Tk theo dõi công nợ',
  ACCOUNT_TYPE_CODE: 'Phân loại tài khoản',
  FX_REVAL_DEBIT_TYPE: 'Pp tính tggs nợ',
  FX_REVAL_CREDIT_TYPE: 'Pp tính tggs có'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.CODE, key: 'code', type: 'main' },
  { header: HEADERS.NAME, key: 'name', type: 'main' },
  { header: HEADERS.PARENT_ACCOUNT_CODE, key: 'tk', type: 'main' },
  { header: HEADERS.TK_SO_CAI, key: 'tk_so_cai', type: 'main' },
  { header: HEADERS.TK_CHI_TET, key: 'tk_chi_tiet', type: 'main' },
  { header: HEADERS.BAC_TK, key: 'bac_tk', type: 'main' },
  { header: HEADERS.CURRENCY_CODE, key: 'currency_code', type: 'main' },
  { header: HEADERS.AR_AP_TRACKING_TYPE, key: 'ar_ap_tracking_type', type: 'main' },
  { header: HEADERS.ACCOUNT_TYPE_CODE, key: 'account_type_code', type: 'main' },
  { header: HEADERS.FX_REVAL_DEBIT_TYPE, key: 'fx_reval_debit_type', type: 'main' },
  { header: HEADERS.FX_REVAL_CREDIT_TYPE, key: 'fx_reval_credit_type', type: 'main' }
];

const parse = {
  string(val: unknown): string | null {
    if (val == null || String(val).trim() === '') return null;
    return String(val).trim();
  },

  number(val: unknown, def = 0): number {
    if (val == null || String(val).trim() === '') return def;
    const n = parseFloat(String(val));
    return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
  },

  boolean(val: unknown, def = false): boolean {
    if (val == null || String(val).trim() === '') return def;
    const s = String(val).trim();
    return ['1', 'true', 'TRUE', 'True'].includes(s);
  },

  arApTrackingType(val: unknown): number {
    if (val == null) return 0;
    const str = String(val).trim();
    const m = str.match(/^(\d)/);
    if (m) return parseInt(m[1], 10);
    return 0;
  },

  accountTypeCode(val: unknown): string {
    if (val == null) return 'KPL';
    const str = String(val).trim().toUpperCase();
    const validCodes = ['KPL', 'TA', 'TI', 'CN', 'NH', 'VV', 'HD', 'KU', 'MR1', 'MR2'];
    for (const code of validCodes) {
      if (str.startsWith(code)) {
        return code;
      }
    }
    return 'KPL';
  },

  fxRevalType(val: unknown): number {
    if (val == null) return 0;
    const str = String(val).trim();
    const m = str.match(/^(\d)/);
    if (m) return parseInt(m[1], 10);
    return 0;
  }
};

type ExcelFieldRows = Record<string, unknown[]>;
type UUIDLookupResponse = { main?: Record<string, Array<string | null | undefined>> };

const UUID_FIELDS = {
  main: ['tk']
};

const getUUID = (uuid: unknown, field: string, i: number): string | null => {
  const value = (uuid as UUIDLookupResponse | null)?.main?.[field]?.[i];
  return value ? String(value) : null;
};

const UUIDPayload = (data: ParsedExcelData) => ({
  main: Object.fromEntries(
    UUID_FIELDS.main.map(f => [
      f,
      (data.main?.[f] || []).map(v => (v == null || String(v).trim() === '' ? '' : String(v).trim()))
    ])
  )
});

const getFieldName = (key: string) =>
  HEADER_CONFIG.find(c => c.key === key)
    ?.header?.replace('*', '')
    .trim() || key;

const ValidateUUID = (response: unknown, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  const main = (response as UUIDLookupResponse | null)?.main;
  if (!main) return [];

  const errors: ValidationError[] = [];

  Object.entries(main).forEach(([field, values]) => {
    if (!Array.isArray(values)) return;

    const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === 'main');
    if (!mapping) return;

    values.forEach((val, i) => {
      // parent_account_code is optional, so we only error if it is present but cannot be resolved
      if (val === null) {
        errors.push({
          row: i + 2,
          field,
          section: 'main',
          columnIndex: mapping.index,
          message: `${getFieldName(field)} không hợp lệ`
        });
      }
    });
  });

  return errors;
};

export type ImportBody = AccountModelInput & {
  tk_so_cai: boolean;
  tk_chi_tiet: boolean;
  bac_tk: number;
};

const REQUIRED_FIELDS = ['code', 'name'] as const;

const hasRequired = (main: ExcelFieldRows, i: number) =>
  REQUIRED_FIELDS.every(f => main[f]?.[i] != null && String(main[f][i]).trim() !== '');

const TransformToBodies = (rows: unknown[][], parsed: ParsedExcelData, uuid: unknown): ImportBody[] => {
  const main = (parsed.main ?? {}) as ExcelFieldRows;

  return rows.reduce<ImportBody[]>((acc, _, i) => {
    if (!hasRequired(main, i)) return acc;

    const code = parse.string(main.code[i]);
    const name = parse.string(main.name[i]);
    if (!code || !name) return acc;

    acc.push({
      code,
      name,
      parent_account_code: getUUID(uuid, 'tk', i),
      tk_so_cai: parse.boolean(main.tk_so_cai?.[i], false),
      tk_chi_tiet: parse.boolean(main.tk_chi_tiet?.[i], false),
      bac_tk: parse.number(main.bac_tk?.[i], 0),
      currency_code: parse.string(main.currency_code?.[i]) ?? '',
      ar_ap_tracking_type: parse.arApTrackingType(main.ar_ap_tracking_type?.[i]),
      account_type_code: parse.accountTypeCode(main.account_type_code?.[i]),
      fx_reval_debit_type: parse.fxRevalType(main.fx_reval_debit_type?.[i]),
      fx_reval_credit_type: parse.fxRevalType(main.fx_reval_credit_type?.[i]),
      active: true,
      balance_type: 'debit',
      role: AccountRole.IN_OTHER,
      constraints: []
    });

    return acc;
  }, []);
};

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
